/**
 * Language utility functions for IVASI multilanguage support
 */

const SUPPORTED_LANGUAGES = ['en', 'de'];
const DEFAULT_LANGUAGE = 'en';

/**
 * Get localized path by adding language prefix
 * @param {string} path - The path without language prefix
 * @param {string} lang - The target language code
 * @returns {string} - Localized path
 */
export const getLocalizedPath = (path, lang) => {
  if (!lang || lang === DEFAULT_LANGUAGE) {
    return path;
  }

  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `/${lang}${cleanPath}`;
};

/**
 * Extract language from pathname
 * @param {string} pathname - The current pathname
 * @returns {string} - Detected language code or default
 */
export const getLanguageFromPath = (pathname) => {
  const pathParts = pathname.split('/').filter(Boolean);
  const firstPart = pathParts[0];

  if (SUPPORTED_LANGUAGES.includes(firstPart)) {
    return firstPart;
  }

  return DEFAULT_LANGUAGE;
};

/**
 * Remove language prefix from pathname
 * @param {string} pathname - The current pathname
 * @returns {string} - Pathname without language prefix
 */
export const removeLanguageFromPath = (pathname) => {
  const pathParts = pathname.split('/').filter(Boolean);

  if (SUPPORTED_LANGUAGES.includes(pathParts[0])) {
    pathParts.shift(); // Remove language code
  }

  return '/' + pathParts.join('/');
};

/**
 * Detect browser language
 * @returns {string} - Detected language code or default
 */
export const detectBrowserLanguage = () => {
  const browserLang = navigator.language.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(browserLang) ? browserLang : DEFAULT_LANGUAGE;
};

/**
 * Get language-specific JSON filename
 * @param {string} pageId - The page identifier
 * @param {string} lang - The language code
 * @returns {string} - Filename with language suffix
 */
export const getLanguageFileName = (pageId, lang) => {
  if (lang === DEFAULT_LANGUAGE) {
    return `${pageId}.json`;
  }
  return `${pageId}.${lang}.json`;
};

/**
 * Check if a language is supported
 * @param {string} lang - The language code to check
 * @returns {boolean} - True if supported
 */
export const isLanguageSupported = (lang) => {
  return SUPPORTED_LANGUAGES.includes(lang);
};

/**
 * Get all supported languages
 * @returns {Array<string>} - Array of supported language codes
 */
export const getSupportedLanguages = () => {
  return [...SUPPORTED_LANGUAGES];
};

/**
 * Get default language
 * @returns {string} - Default language code
 */
export const getDefaultLanguage = () => {
  return DEFAULT_LANGUAGE;
};

export default {
  getLocalizedPath,
  getLanguageFromPath,
  removeLanguageFromPath,
  detectBrowserLanguage,
  getLanguageFileName,
  isLanguageSupported,
  getSupportedLanguages,
  getDefaultLanguage,
};
